import React, { useState, useEffect, useCallback } from 'react';
import {
  initSocket,
  subscribeToStopoutNotifications,
  subscribeToSocketStatus
} from '../services/socketService';

const StopLossNotification = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Ensure the polling channel is active
  useEffect(() => {
    initSocket();
  }, []);

  // Monitor connection status
  useEffect(() => {
    const unsubscribe = subscribeToSocketStatus((status) => {
      setIsConnected(status === 'connected');
    });

    return () => unsubscribe();
  }, []);

  const dismissNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  const handleStopoutNotification = useCallback((data) => {
    // Determine if the trade is profitable or not based on gain
    const isProfit = data.gain !== null && data.gain !== undefined && data.gain > 0;
    const notificationType = isProfit ? 'success' : 'danger'; // Green for profit, red for loss
    const borderColor = isProfit ? '#28a745' : '#dc3545'; // Green or red border

    const notification = {
      id: `stopout-${Date.now()}-${Math.random()}`,
      type: notificationType,
      ticker: data.ticker || 'Unknown',
      accountName: data.account_name || 'Unknown Account',
      triggerPrice: data.trigger_price,
      shieldPrice: data.shield_price,
      shieldName: data.shield_name || 'Stop Loss',
      timestamp: new Date(data.triggered_at || Date.now()).toLocaleTimeString(),
      gain: data.gain,
      gainPct: data.gain_pct,
      purchasePrice: data.purchase_price,
      borderColor: borderColor,
      // NO autoClose - stays until manually dismissed
    };

    setNotifications(prev => [...prev, notification]);
  }, []);

  // Subscribe to stopout notifications
  useEffect(() => {
    const unsubscribe = subscribeToStopoutNotifications((data) => {
      if (data) {
        console.log('🛡️ Stop-loss triggered:', data);
        handleStopoutNotification(data);
      }
    });

    return () => unsubscribe();
  }, [handleStopoutNotification]);

  return (
    <div className="stopout-notification-container">
      {/* Stop-loss notifications */}
      <div style={{
        position: 'fixed',
        top: '80px',
        right: '20px',
        zIndex: 1060,  // Higher than BalanceUpdateAlert (1050)
        maxWidth: '450px'
      }}>
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`alert alert-${notification.type} alert-dismissible fade show`}
            role="alert"
            style={{
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              border: `2px solid ${notification.borderColor}`
            }}
          >
            <div className="d-flex align-items-start">
              <div className="me-2" style={{ fontSize: '1.5rem' }}>🛡️</div>
              <div className="flex-grow-1">
                <strong style={{ fontSize: '1.1rem' }}>Stop Loss Triggered!</strong>
                <br />
                <div className="mt-2">
                  <strong>{notification.ticker}</strong> in <strong>{notification.accountName}</strong>
                </div>
                <div className="mt-1" style={{ fontSize: '0.9rem' }}>
                  Shield: <strong>{notification.shieldName}</strong> at <strong>${notification.shieldPrice?.toFixed(2)}</strong>
                  <br />
                  Trigger Price: <strong>${notification.triggerPrice?.toFixed(2)}</strong>
                  {notification.purchasePrice !== null && notification.purchasePrice !== undefined && (
                    <>
                      <br />
                      Purchase Price: <strong>${notification.purchasePrice?.toFixed(2)}</strong>
                    </>
                  )}
                  {notification.gain !== null && notification.gain !== undefined && (
                    <>
                      <br />
                      <strong style={{ color: notification.gain >= 0 ? '#28a745' : '#dc3545' }}>
                        {notification.gain >= 0 ? '+' : ''}{notification.gain?.toFixed(2)}
                        ({notification.gain >= 0 ? '+' : ''}{notification.gainPct?.toFixed(2)}%)
                      </strong>
                    </>
                  )}
                </div>
                <div className="mt-1">
                  <small className="text-muted">Triggered at {notification.timestamp}</small>
                </div>
              </div>
              <button
                type="button"
                className="btn-close ms-2"
                onClick={() => dismissNotification(notification.id)}
                aria-label="Close"
                style={{ fontSize: '0.9rem' }}
              ></button>
            </div>
          </div>
        ))}
      </div>

      {/* Connection status indicator */}
      {process.env.NODE_ENV === 'development' && (
        <div
          className="status-indicator-wrapper"
          style={{
            position: 'fixed',
            bottom: '50px',
            right: '10px',
            zIndex: 1000,
            cursor: 'pointer'
          }}
        >
          <style>{`
            .status-indicator-wrapper {
              position: relative;
            }
            .status-indicator-wrapper .tooltip-bubble {
              visibility: hidden;
              opacity: 0;
              position: absolute;
              bottom: calc(100% + 8px);
              right: 0;
              padding: 6px 12px;
              background-color: rgba(0, 0, 0, 0.85);
              color: white;
              font-size: 12px;
              white-space: nowrap;
              border-radius: 6px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              transition: opacity 0.3s ease, visibility 0.3s ease;
              pointer-events: none;
              z-index: 10000;
            }
            .status-indicator-wrapper .tooltip-bubble::after {
              content: "";
              position: absolute;
              top: 100%;
              right: 10px;
              border: 5px solid transparent;
              border-top-color: rgba(0, 0, 0, 0.85);
            }
            .status-indicator-wrapper:hover .tooltip-bubble {
              visibility: visible;
              opacity: 1;
            }
          `}</style>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            backgroundColor: isConnected ? '#28a745' : '#dc3545',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            transition: 'all 0.3s ease'
          }}>
            <span style={{ fontSize: '14px', filter: 'brightness(0) invert(1)' }}>🛡️</span>
          </div>
          <div className="tooltip-bubble">
            Stop-Loss Alerts: {isConnected ? 'Connected' : 'Disconnected'}
          </div>
        </div>
      )}
    </div>
  );
};

export default StopLossNotification;
